import {
  clientsOpportunitiesListQueryKey,
  customFieldsListOptions,
  EntityTypeEnum,
  leadsLogsListQueryKey,
  leadsOpportunitiesListQueryKey,
  leadsRetrieveOptions,
  opportunitiesCreateMutation,
  opportunitiesListQueryKey,
  opportunitiesPipelineListQueryKey,
  opportunitiesPipelinesListOptions,
  Type83fEnum
} from '@crm/api'
import { OpportunityForm } from '@crm/components'
import {
  defaultNonPagination,
  getSelectIdValue,
  getUserFullName,
  transformCustomFieldValues
} from '@crm/utils'
import { useInvalidatePredicateQueries, useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { DEFAULT_ISO_DATE_FORMAT, format, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Flex, Form, Modal } from 'antd'
import { useEffect, useMemo } from 'react'

const formName = 'create-opportunity-from-status'

type Props = {
  open: boolean
  leadId?: number
  clientId?: string
  onSuccess: () => void
  onCancel: () => void
}

export const CreateOpportunityModal = ({
  open,
  leadId,
  clientId,
  onSuccess,
  onCancel
}: Props) => {
  const { t } = useLocales()
  const { user } = useSessionContext()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm()

  const { data: lead, isLoading: isLeadLoading } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(leadId) } }),
    enabled: !!leadId && open
  })

  const { data: customFields } = useQuery({
    ...customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.OPPORTUNITY],
        ...defaultNonPagination
      }
    }),
    enabled: open
  })

  const { data: opportunityPipelineData } = useQuery({
    ...opportunitiesPipelinesListOptions({
      query: { page: 1, per_page: 1, ordering: '-is_default' }
    }),
    enabled: open
  })

  const { filteredCustomFields, checkboxCustomFields } = useMemo(() => {
    const availableCustomFields =
      customFields?.results?.filter((e) => !e.deleted_at) || []

    const grouped = availableCustomFields.reduce((acc, e) => {
      const key =
        e.type === Type83fEnum.BOOLEAN
          ? 'checkboxCustomFields'
          : 'filteredCustomFields'

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(e)
      return acc
    }, {} as Record<'checkboxCustomFields' | 'filteredCustomFields', typeof availableCustomFields>)

    return {
      filteredCustomFields: grouped.filteredCustomFields || [],
      checkboxCustomFields: grouped.checkboxCustomFields || []
    }
  }, [customFields?.results])

  useEffect(() => {
    if (!open) return

    const defaultPipeline = opportunityPipelineData?.results?.[0]?.is_default
      ? opportunityPipelineData.results[0]
      : undefined

    form.setFieldsValue({
      lead: leadId,
      client: clientId ? Number(clientId) : undefined,
      contact: lead?.contact?.id,
      owner: defaultPipeline?.responsible?.name ?? getUserFullName(user?.user),
      pipeline: defaultPipeline?.id
    })
  }, [open, lead, opportunityPipelineData, form, user])

  const { mutate: createMutate, isPending } = useMutation({
    ...opportunitiesCreateMutation(),
    onError: (e: any) => showApiErrors(e, form),
    onSuccess: async (opportunity) => {
      await invalidateQueries([
        leadsOpportunitiesListQueryKey({
          path: { lead_id: String(leadId) }
        }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesListQueryKey(),
        opportunitiesPipelineListQueryKey({
          query: { status: [opportunity?.status?.id] }
        }),
        clientsOpportunitiesListQueryKey({ path: { client_id: clientId! } })
      ])

      onSuccess()
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const { close_date, owner, product, ...rest } = values

    const transformedCustomFields = customFields?.results
      ? transformCustomFieldValues(
          rest?.custom_field_values,
          customFields.results
        )
      : []

    const requestBody = {
      ...rest,
      close_date: close_date && format(close_date, DEFAULT_ISO_DATE_FORMAT),
      owner: getSelectIdValue(owner, user?.user.sso_id ?? undefined),
      product: getSelectIdValue(product, undefined),
      custom_field_values: transformedCustomFields
    }

    createMutate({ body: requestBody })
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={t('opportunity.add_opportunity')}
      destroyOnHidden
      loading={isLeadLoading}
      width={900}
      footer={
        <Flex justify="end" gap={8}>
          <Button disabled={isPending} onClick={onCancel}>
            {t('buttons.cancel')}
          </Button>

          <Button type="primary" loading={isPending} onClick={onSubmit}>
            {t('buttons.save')}
          </Button>
        </Flex>
      }
    >
      <Form name={formName} form={form} onFinish={onSubmit} layout="vertical">
        <OpportunityForm
          form={form}
          checkboxCustomFields={checkboxCustomFields}
          filteredCustomFields={filteredCustomFields}
          leadId={leadId}
          clientId={clientId}
        />
      </Form>
    </Modal>
  )
}
